import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { dmY } from "@/lib/dateFmt";
import { APP_VERSION, APP_BUILD, APP_CODENAME } from "@/lib/version";
import { Sparkle, CaretDown, ArrowClockwise } from "@phosphor-icons/react";

/**
 * "What's New" card for the Admin Dashboard. Shows the currently-deployed
 * backend version + a collapsible history of recent releases with their
 * bullet notes. Lets the admin verify after each Re-publish that the new
 * version actually took effect on this environment.
 */
export default function ReleaseNotesCard() {
    const [current, setCurrent] = useState({ version: APP_VERSION, notes: [], title: "" });
    const [releases, setReleases] = useState([]);
    const [open, setOpen] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [v, h] = await Promise.all([api.get("/version"), api.get("/version/history")]);
            setCurrent(v.data);
            setReleases(h.data.releases || []);
        } catch { /* keep local fallback */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // Compare backend version with bundled frontend version — mismatch means
    // one side hasn't reloaded yet.
    const frontendVersion = APP_VERSION;
    const drift = current.version && current.version !== frontendVersion;

    const visible = showAll ? releases : releases.slice(0, 3);

    return (
        <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-emerald-50/40 via-white to-blue-50/40 p-4 shadow-sm" data-testid="release-notes-card">
            <div
                role="button"
                tabIndex={0}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
                className="flex w-full cursor-pointer items-center justify-between text-left"
                data-testid="release-notes-toggle"
            >
                <div className="flex items-start gap-2">
                    <Sparkle size={16} weight="fill" className="mt-0.5 text-amber-500" />
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">What's New</div>
                        <div className="mt-0.5 font-display text-base font-bold text-slate-900" data-testid="release-notes-current">
                            MICM v{current.version || frontendVersion}
                            {current.title && <span className="ml-1.5 text-sm font-medium text-slate-600">· {current.title}</span>}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500">
                            build {APP_BUILD} · {APP_CODENAME}
                            {drift && (
                                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-amber-800" data-testid="release-notes-drift">
                                    ⚠ Refresh — backend on v{current.version}, this tab on v{frontendVersion}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); load(); }} disabled={loading} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Refresh version info">
                        <ArrowClockwise size={13} className={loading ? "animate-spin" : ""} />
                    </button>
                    <CaretDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </div>

            {open && (
                <div className="mt-3 space-y-3" data-testid="release-notes-body">
                    {current.notes && current.notes.length > 0 && (
                        <div>
                            <div className="mb-1 text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Latest changes</div>
                            <ul className="ml-5 list-disc space-y-0.5 text-xs text-slate-700">
                                {current.notes.map((n, i) => <li key={i}>{n}</li>)}
                            </ul>
                        </div>
                    )}
                    {releases.length > 1 && (
                        <div className="border-t border-slate-100 pt-3">
                            <div className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Previous releases</div>
                            <ul className="space-y-2">
                                {visible.filter((r) => r.version !== current.version).map((r) => (
                                    <li key={r.version} className="rounded border border-slate-100 bg-white/60 p-2" data-testid={`release-${r.version}`}>
                                        <div className="flex items-baseline justify-between">
                                            <div className="font-mono text-xs font-semibold text-slate-800">v{r.version}</div>
                                            <div className="text-[10px] text-slate-400">{dmY(r.date)}</div>
                                        </div>
                                        {r.title && <div className="mt-0.5 text-[11px] font-medium text-slate-700">{r.title}</div>}
                                        {r.notes && (
                                            <ul className="ml-4 mt-1 list-disc space-y-0.5 text-[11px] text-slate-600">
                                                {r.notes.map((n, i) => <li key={i}>{n}</li>)}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>
                            {releases.length > 3 && (
                                <button
                                    type="button"
                                    onClick={() => setShowAll((v) => !v)}
                                    className="mt-2 text-[11px] font-medium text-blue-700 hover:underline"
                                    data-testid="release-notes-show-all"
                                >
                                    {showAll ? "Show fewer" : `Show all ${releases.length - 1} previous releases`}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
